import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LearningActivity } from "types";
import { PollingData } from "../../components";
import { 
  useEditionsQuery, 
  useEnrollmentStatusQuery,
  useInvalidateEditionsQuery,
  usePatchMutation } from "../../queries";
import { useAuthStore } from "../../stores/authStore";
import { getError } from "../../utils/error";
import { useRouteParamsLo } from "../../utils/useRouteParamsLo";

// Polling storage utilities
const EDITION_CHANGE_POLLING_KEY = "edition_change_polling";

const clearEditionChangePollingFromStorage = () => {
  sessionStorage.removeItem(EDITION_CHANGE_POLLING_KEY);
};

const getEditionChangePollingFromStorage = (): PollingData | null => {
  const data = sessionStorage.getItem(EDITION_CHANGE_POLLING_KEY);
  return data ? JSON.parse(data) : null;
};

export const useEditions = (
  learningObject: LearningActivity | null, 
  setShowModal?: (show: boolean) => void, 
  setModalText?: (text: string) => void,
  setIconType?: (iconType: "success" | "error" | "warning") => void 
) => {

  const { id, learningObjectTypology } = learningObject ?? {};
  
  const {
    idString
  } = useRouteParamsLo();

  const pollingCompletedRef = useRef<boolean>(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const userId = useAuthStore(state => state.userData?.id_user) ?? 0;
  const corporateId = useAuthStore(state => state.userData?.organization_id);
  
  const [newEditionIdForPolling, setNewEditionIdForPolling] = useState<number | null>(null);
  const [isPollingEditionChange, setIsPollingEditionChange] = useState(false);
  
  const { t } = useTranslation();

  const { 
    data: editions ,
    refetch: refetchEditions,
    isFetching: editionsIsFetching
  } = useEditionsQuery({
    classroomId: id?.toString() ?? ""
  });

  const {
    mutate: changeEditionMutate,
    isPending: isChangingEdition
  } = usePatchMutation({
    learningObjectId: id ?? 0,
    learningObjectTypology: learningObjectTypology ?? "",
    isToj: learningObject?.isToj ?? false
  });
    
  const { invalidateEditions } = useInvalidateEditionsQuery({
    classroomId: idString  ?? "",
    scrollToTop: false
  });

  // Enrollment status query for edition change polling
  const {
    data: editionChangeStatusData, 
    refetch: refetchEditionChangeStatus 
  } = useEnrollmentStatusQuery({
    corporateId: corporateId ?? 0,
    editionId: newEditionIdForPolling ?? 0,
    enabled: false, // Disabled, use manual refetch only
    userId
  });

  const saveEditionChangePollingToStorage = (editionId: number, userId: number) => {
    sessionStorage.setItem(EDITION_CHANGE_POLLING_KEY, JSON.stringify({
      editionId,
      startTime: Date.now(),
      userId
    }));
  };

  const handleEditionChange = useCallback((newEditionId: number) => {
    changeEditionMutate(newEditionId, {
      onError: (error) => {
        setIconType && setIconType("error");
          
        const parsedError = getError(error);
        const maximumError = parsedError?.[0]?.message.includes("participants maximum number");
        const alreadyAssociatedError = parsedError?.[0]?.message.includes("already associated with user");
        const maxCapacityReached = parsedError?.[0]?.message.includes("MAX_EDITION_CAPACITY_REACHED_DURING_UPDATE_ENROLLMENTS");
  
        if (maxCapacityReached) {
          setShowModal && setShowModal(true);
          setModalText && setModalText(t("max-capacity-reached-error"));
          invalidateEditions();
          refetchEditions();
          return;
        }
  
        if (parsedError?.[0]?.name === "FATAL") {
          //@ts-ignore
          const errorKey = JSON.parse(error as string)?.errors["FATAL"]?.[0]?.key;
          if (errorKey === "bad.request.user.already.associated") {
            
            setModalText && setModalText(t("already-associated-error"));
          } else {
            setModalText &&  setModalText(parsedError?.[0]?.message || t("generic_error"));
          }
        } else if (maximumError) {
          setModalText && setModalText(t("maximum-participants-error"));
        } else if (alreadyAssociatedError) {
          setModalText && setModalText(t("already-associated-error"));
        } else {
          setModalText && setModalText(t("enrollment-generic-error"));
        }
  
        invalidateEditions();
        setShowModal && setShowModal(true);
      },
      onSuccess: () => {
  
        // Set the new edition ID for polling
        setNewEditionIdForPolling(newEditionId);
  
        // Reset polling completed flag
        pollingCompletedRef.current = false;
  
        // Save to storage and start polling
        saveEditionChangePollingToStorage(newEditionId, userId);
        setIsPollingEditionChange(true);
  
        // Invalidate to prepare for updates
        invalidateEditions();
        refetchEditions();
      }
    });
  }, [changeEditionMutate, invalidateEditions, refetchEditions, t, userId]);

  // Polling logic with progressive intervals
  useEffect(() => {
    if (!isPollingEditionChange) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      return;
    }
  
    const startTime = Date.now();
    const MAX_POLLING_DURATION = 120000; // 2 minutes
    const MAX_INTERVAL = 20000; // 20 seconds
    let currentInterval = 5000; // Start with 5 seconds
  
    const scheduleNextPoll = () => {
      const elapsed = Date.now() - startTime;
  
      if (elapsed >= MAX_POLLING_DURATION) {
        // Timeout reached
        pollingCompletedRef.current = true;
        setIsPollingEditionChange(false);
        clearEditionChangePollingFromStorage();
        setModalText && setModalText(t("enrollment-polling-error"));
        setShowModal && setShowModal(true);
        // invalidateLearningObject();
        invalidateEditions();
        refetchEditions();
        return;
      }
  
      // Refetch enrollment status
      refetchEditionChangeStatus();
  
      // Increment interval by 5s if below max
      if (currentInterval < MAX_INTERVAL) {
        currentInterval += 5000;
      }
  
      // Schedule next poll
      pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);
    };
  
    // First call after initial interval (5s)
    pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);
  
    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [isPollingEditionChange, refetchEditionChangeStatus, invalidateEditions, refetchEditions, t]);

  // Handle edition change status response
  useEffect(() => {
    if (!editionChangeStatusData?.status) return;
  
    // If polling is already completed, do nothing
    if (pollingCompletedRef.current) return;
  
    // Only process status changes if polling is active
    if (!isPollingEditionChange) return;
  
    const currentStatus = editionChangeStatusData.status;
    const currentErrorMessage = editionChangeStatusData.errorMessage;
  
    if (currentStatus === "IN_PROGRESS") {
      // Already polling, continue
      // Status is still in progress, keep polling
    } else if (currentStatus === "COMPLETE" || currentStatus === "ERROR") {
      // Mark polling as completed FIRST to avoid loops
      pollingCompletedRef.current = true; 
      // Stop polling
      setIsPollingEditionChange(false);
  
      // Remove from sessionStorage
      clearEditionChangePollingFromStorage();
  
      // Show modal with result
      if (currentStatus === "COMPLETE") {
        setModalText && setModalText(t("enrollment-submitted-success"));
        setIconType && setIconType("success");
        setShowModal && setShowModal(true);
        pollingCompletedRef.current = true;
      } else if (currentStatus === "ERROR" && currentErrorMessage.includes("[GET-ENROLLMENT-STATUS-ERROR]")){
        setModalText && setModalText(t("max-capacity-reached-error"));
        setIconType && setIconType("error");
        setShowModal && setShowModal(true);
      } else {
        setModalText && setModalText(t("enrollment-polling-error"));
        setIconType && setIconType("error");
        setShowModal && setShowModal(true);
      }
  
      // Invalidate and refetch
      // invalidateLearningObject();
      invalidateEditions();
      refetchEditions();
    }
    // NOT_ENROLLED: do nothing, normal behavior
  }, [editionChangeStatusData?.status, isPollingEditionChange, invalidateEditions, refetchEditions, t]);

  // Restore polling from sessionStorage on mount
  useEffect(() => {
    const savedPolling = getEditionChangePollingFromStorage();
    if (savedPolling && savedPolling.userId === userId) {
      // Resume polling with the saved edition ID
      setNewEditionIdForPolling(savedPolling.editionId);
      pollingCompletedRef.current = false;
      setIsPollingEditionChange(true);
    }
  }, [userId]);

  return {
    editions,
    refetchEditions,
    isChangingEdition,
    editionsIsFetching,
    handleEditionChange,
    isPollingEditionChange
  };
};
